import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

// Comic "action" buttons: ink border, hard offset shadow, and a press-down
// (shadow collapses, button shifts into its own shadow) on :active.
const ACTION =
  'border-2 border-border shadow-[3px_3px_0_0_var(--color-border)] hover:-translate-x-px hover:-translate-y-px hover:shadow-[4px_4px_0_0_var(--color-border)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-display tracking-wide uppercase transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground hover:bg-primary ${ACTION}`,
        destructive: `bg-destructive text-white hover:bg-destructive focus-visible:ring-destructive/20 ${ACTION}`,
        outline: `bg-card text-foreground hover:bg-accent hover:text-accent-foreground ${ACTION}`,
        secondary: `bg-secondary text-secondary-foreground hover:bg-secondary ${ACTION}`,
        ghost: 'rounded-md hover:bg-accent hover:text-accent-foreground',
        link: 'normal-case tracking-normal font-sans text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-12 rounded-md px-7 text-base has-[>svg]:px-5',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
